import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from '../i18n';
import { PageHeader } from '../components/Section';

export default function ChangePassword() {
  const t = useTranslation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPassword !== confirm) {
      setError(t.changePassword.errors.mismatch);
      return;
    }
    if (newPassword.length < 8) {
      setError(t.changePassword.errors.tooShort);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/account/password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(data.error || t.changePassword.errors.generic);
        return;
      }
      setSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirm('');
    } catch {
      setError(t.changePassword.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader title={t.changePassword.title} description={t.changePassword.description} />
      <Paper sx={{ p: 3, maxWidth: 480 }}>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2}>
            {success && <Alert severity="success">{t.changePassword.success}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              type="password"
              label={t.changePassword.oldPassword}
              autoComplete="current-password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />
            <TextField
              type="password"
              label={t.changePassword.newPassword}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <TextField
              type="password"
              label={t.changePassword.confirmPassword}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            <Box>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? t.common.loading : t.changePassword.submit}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
